import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Save, Play, ExternalLink, AlertCircle, AlertTriangle, CheckCircle2, Building2 } from "lucide-react";
import { toast } from "sonner";
import { SchemaTemplates } from "./SchemaTemplates";
import { SchemaOverrideEditor } from "./SchemaOverrideEditor";
import { RawHtmlCheck } from "./RawHtmlCheck";
import { SchemaRepairs, SchemaHistory } from "./SchemaRepairs";

const BIZ = [
  ["biz_legal_name", "Registered business name"],
  ["biz_trading_name", "Trading name"],
  ["biz_phone", "Phone"],
  ["biz_email", "Email"],
  ["biz_street", "Street address"],
  ["biz_locality", "Suburb / city"],
  ["biz_region", "Region"],
  ["biz_postcode", "Postcode"],
  ["biz_country", "Country"],
  ["biz_hours", "Opening hours (e.g. Mo-Fr 08:30-17:00)"],
];

export default function SchemaTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [audit, setAudit] = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)).catch(() => setS(false)); }, []);
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));

  const save = async () => {
    setSaving(true);
    const payload = {};
    BIZ.forEach(([k]) => (payload[k] = s[k] || ""));
    payload.biz_socials = (typeof s.biz_socials === "string" ? s.biz_socials.split(/[\n,]/) : (s.biz_socials || []))
      .map((x) => x.trim()).filter(Boolean);
    try { const r = await api.put("/admin/settings", payload); setS(r.data); toast.success("Business identity saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const runAudit = async () => {
    setRunning(true);
    try {
      const r = await api.get("/admin/seo/schema/audit", { params: { base: window.location.origin, limit: 500 } });
      setAudit(r.data);
    } catch { toast.error("Audit failed"); }
    finally { setRunning(false); }
  };

  if (s === false) return <p className="text-sm text-destructive">Could not load settings.</p>;
  if (!s) return <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</p>;
  const socials = typeof s.biz_socials === "string" ? s.biz_socials : (s.biz_socials || []).join("\n");

  return (
    <div className="space-y-8 max-w-5xl" data-testid="schema-tab">
      <RawHtmlCheck />
      <SchemaTemplates />
      <section>
        <h3 className="font-bold text-steel flex items-center gap-2 mb-1"><Building2 size={18} /> Business Identity (Organization &amp; LocalBusiness)</h3>
        <p className="text-xs text-muted-foreground mb-3">Used for homepage Organization / LocalBusiness schema. Only real, verified details — LocalBusiness publishes once a street address + phone are present. Logo comes from the Branding tab.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {BIZ.map(([k, label]) => (
            <div key={k}>
              <Label className="text-xs font-semibold text-steel">{label}</Label>
              <Input value={s[k] || ""} onChange={(e) => set(k, e.target.value)} className="mt-1 h-9" data-testid={`biz-${k}`} />
            </div>
          ))}
        </div>
        <div className="mt-3">
          <Label className="text-xs font-semibold text-steel">Social profile URLs (one per line)</Label>
          <Textarea rows={3} value={socials} onChange={(e) => set("biz_socials", e.target.value)} className="mt-1" data-testid="biz-socials" />
        </div>
        <Button className="mt-3 bg-accent text-steel hover:bg-amber-600 font-semibold" onClick={save} disabled={saving} data-testid="schema-save-biz">
          {saving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Save size={14} className="mr-1.5" />} Save business identity
        </Button>
      </section>

      <section>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-bold text-steel">Structured Data Audit</h3>
          <Button size="sm" onClick={runAudit} disabled={running} className="bg-steel text-white hover:bg-steel/90" data-testid="schema-run-audit">
            {running ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Play size={14} className="mr-1.5" />} Run audit
          </Button>
        </div>
        {!audit ? <p className="text-sm text-muted-foreground">Run an audit to validate generated Product &amp; Organization schema against Google's requirements.</p>
          : (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <Stat label="Products sampled" value={audit.sampled} />
                <Stat label="Schema OK" value={audit.product_ok} tone="good" />
                <Stat label="Need attention" value={audit.product_issues} tone={audit.product_issues ? "warn" : "good"} />
              </div>
              {audit.org_issues?.length > 0 && (
                <div className="rounded-xl border bg-white p-3 space-y-1" data-testid="schema-org-issues">
                  {audit.org_issues.map((o, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-steel"><AlertTriangle size={13} className="text-amber-500" /> {o.msg}</div>
                  ))}
                </div>
              )}
              <div className="rounded-xl border bg-white divide-y max-h-[50vh] overflow-y-auto" data-testid="schema-audit-rows">
                {audit.rows.length === 0 ? <p className="p-4 text-sm text-emerald-600 flex items-center gap-2"><CheckCircle2 size={15} /> No product schema errors in the sample.</p>
                  : audit.rows.map((r, i) => (
                    <div key={i} className="p-3 text-sm">
                      <div className="flex items-center gap-2">
                        {r.severity === "error" ? <AlertCircle size={14} className="text-red-600" /> : <AlertTriangle size={14} className="text-amber-500" />}
                        <a href={r.url} target="_blank" rel="noreferrer" className="font-semibold text-steel hover:text-accent line-clamp-1">{r.name}</a>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 ml-6">{r.issues.join(" · ")}</div>
                    </div>
                  ))}
              </div>
            </div>
          )}
      </section>

      <SchemaOverrideEditor />
      <SchemaRepairs />
      <SchemaHistory />

      <section>
        <h3 className="font-bold text-steel mb-2">Validate with Google &amp; Schema.org</h3>
        <p className="text-xs text-muted-foreground mb-3">Local validation above checks structure only — a valid schema doesn't guarantee Google indexing or rich-result display. Confirm live eligibility with Google's official tools:</p>
        <div className="flex flex-wrap gap-2">
          <LinkBtn href="https://search.google.com/test/rich-results" label="Google Rich Results Test" />
          <LinkBtn href="https://validator.schema.org/" label="Schema.org Validator" />
          <LinkBtn href="https://search.google.com/search-console" label="Search Console" />
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const c = { good: "text-emerald-600", warn: "text-amber-600" }[tone] || "text-steel";
  return (
    <div className="rounded-xl border bg-white p-4">
      <div className={`text-2xl font-extrabold font-mono ${c}`}>{Number(value || 0).toLocaleString()}</div>
      <div className="text-xs text-muted-foreground mt-1">{label}</div>
    </div>
  );
}
function LinkBtn({ href, label }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border bg-white px-3 py-2 text-sm font-semibold text-steel hover:border-accent">
      {label} <ExternalLink size={13} />
    </a>
  );
}
